import { Component } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * Clears a caught error once the user has navigated somewhere else.
   *
   * This used to be done by keying the boundary on `location.key`, which is a
   * new value on every navigation — so React tore down and rebuilt the entire
   * app subtree, `AuthProvider` included, on every single route change. That is
   * what refired `/me` per page and reset the sidebar and every query
   * subscription along with it. Resetting state only when there is an error to
   * reset costs nothing on the happy path.
   */
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.locationKey !== this.props.locationKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    console.error("Unhandled render error:", error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  handleGoHome = () => {
    this.setState({ error: null });
    this.props.navigate("/", { replace: true });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="flex min-h-svh flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <AlertTriangle className="size-6 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-base font-semibold text-foreground">Something went wrong</h1>
        <p className="max-w-prose text-sm text-muted-foreground">
          This page hit an unexpected error. You can try again, or go back to your invoices.
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={this.handleRetry}>
            Try again
          </Button>
          <Button type="button" size="sm" onClick={this.handleGoHome}>
            Back to invoices
          </Button>
        </div>
      </main>
    );
  }
}

/**
 * `location.key` is passed as a plain prop, never as a `key` — the boundary has
 * to survive navigation, not be replaced by it.
 */
export function ErrorBoundary({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ErrorBoundaryInner locationKey={location.key} navigate={navigate}>
      {children}
    </ErrorBoundaryInner>
  );
}
